import { log } from "@app/app/services/log";
import { merge } from "@app/common/utils/objectUtils";
import * as Sentry from "@sentry/react";
import { z, ZodError, ZodObject } from "zod/v4";

import { loadDebugConfig } from "./loaders/debugLoader";
import { loadEnvConfig } from "./loaders/envLoader";
import { loadHostConfig } from "./loaders/hostLoader";
import { AnalyticsConfigSchema } from "./types/analytics";
import { ApiConfigSchema } from "./types/api";
import { CoreConfigSchema } from "./types/core";
import { DebugConfigSchema } from "./types/debug";
import { IBEConfigSchema } from "./types/ibe";
import { PaymentConfigSchema } from "./types/payment";
import { ThirdPartyConfigSchema } from "./types/third-party";

// Define the final merged configuration shape using Zod schemas
// This provides strong typing and validation for the final config object
const MergedConfigSchema = z.object({
    core: CoreConfigSchema,
    api: ApiConfigSchema,
    analytics: AnalyticsConfigSchema,
    payment: PaymentConfigSchema,
    thirdParty: ThirdPartyConfigSchema,
    debug: DebugConfigSchema,
    ibe: IBEConfigSchema,
});

export type TMergedConfig = z.infer<typeof MergedConfigSchema>;

/**
 * ConfigManager manages the application configuration by loading from various sources,
 * merging them, and validating the final structure.
 */
class ConfigManager {
    // Store the fully merged and validated configuration
    public config: TMergedConfig = {} as TMergedConfig;
    public isInitialized = false;
    private errorsDuringInitialization: Array<[string, ZodError]> = [];

    public constructor() {
        // for now we only initialize on app load
        this.initialize();
    }

    public async initialize(): Promise<void> {
        if (this.isInitialized) {
            log.warn("ConfigManager already initialized.");
            return;
        }

        // Load and validate configuration from environment, host, and debug sources
        // Each loader has specific allowed schemas to prevent unauthorized overrides
        try {
            // 1. Load Env Config
            const envDataRaw = loadEnvConfig();
            log.debug("Loaded env config:", envDataRaw);
            const allowedEnvSchemas = z.object({
                core: CoreConfigSchema,
                api: ApiConfigSchema,
                analytics: AnalyticsConfigSchema,
                payment: PaymentConfigSchema,
                thirdParty: ThirdPartyConfigSchema,
            });
            const envData = this.validateConfigObject("EnvConfigSchema", envDataRaw, allowedEnvSchemas);

            // 2. Load Host Config
            const hostDataRaw = loadHostConfig(envData.core);
            log.debug("Loaded host config:", hostDataRaw);
            const allowedHostSchemas = z.object({
                ibe: IBEConfigSchema,
                debug: DebugConfigSchema,
            });
            const hostData = this.validateConfigObject("HostConfigSchema", hostDataRaw, allowedHostSchemas);

            // 3. Load Debug Config (synchronous) allowed to override any key
            const debugData = loadDebugConfig(envData.core);
            log.debug("Loaded debug config:", debugData);

            // Merge data according to priority
            const mergedData = merge(envData, hostData, debugData);

            // finally, validate the entire config object to make sure there are no debug options that are broken
            this.config = this.validateConfigObject("MergedConfigSchema", mergedData, MergedConfigSchema);

            // log errors if there are any
            if (this.errorsDuringInitialization.length > 0) {
                const errorMessage = this.errorsDuringInitialization
                    .map(([name, error]) => `${name}: ${z.prettifyError(error)}`)
                    .join("\n");
                log.error("Configuration validation errors:", errorMessage);
                // send errors to sentry
                Sentry.captureException(errorMessage);
            }

            this.isInitialized = true;
            log.info("Configuration Initialized"); // Log loaded config for debugging
            log.debug("Configuration:", this.config);
        } catch (error: unknown) {
            const errorMessage = error instanceof Error ? error.message : "Unknown error occurred";
            // Log the initialization error
            log.error(`Failed to initialize configuration: ${errorMessage}`);
            // Optionally re-throw or set a failed state
            throw new Error(`Failed to initialize configuration: ${errorMessage}`);
        }
    }

    public isTestMode(): boolean {
        return this.config.core.appEnv === "test";
    }

    public isDevMode(): boolean {
        return this.config.core.appEnv === "development";
    }

    public isProdMode(): boolean {
        return this.config.core.appEnv === "production";
    }

    /**
     * Validates a config object against a zod schema.
     * Returns the parsed config object if successful, otherwise returns the original config object.
     * @todo: make validation errors throw an error for now we just log them
     */
    private validateConfigObject<T extends ZodObject>(
        validationName: string,
        configObject: Record<string, unknown>,
        schema: T
    ): z.infer<T> {
        const parseResult = schema.safeParse(configObject);

        if (parseResult.success) {
            return parseResult.data;
        } else {
            this.errorsDuringInitialization.push([validationName, parseResult.error]);
            return configObject as z.infer<T>;
        }
    }
}

// --- Boot Configuration --- (Keep as is, separate concern)
export function getBootConfiguration() {
    const shouldIgnoreDelay = localStorage.getItem("hc-ibe-ignore-delay");
    const delayBoot = document.location.href.includes("aethos") && (!shouldIgnoreDelay || shouldIgnoreDelay !== "true");

    return {
        bootDelay: delayBoot ? 0 : undefined,
    };
}

// --- Singleton Instance ---
export const configManager = new ConfigManager();
